<template>
  <div>
    <!-- Loading State -->
    <div v-if="loading" class="bg-white rounded-lg shadow-lg p-8">
      <div class="text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
        <p class="text-gray-600">Cargando constancia...</p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="bg-white rounded-lg shadow-lg p-8">
      <div class="text-center">
        <div class="mb-4">
          <svg class="w-16 h-16 text-red-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        </div>
        <h2 class="text-xl font-bold text-gray-900 mb-2">Error al cargar el documento</h2>
        <p class="text-gray-600">{{ error }}</p>
      </div>
    </div>

    <!-- Constancia Content -->
    <div v-else-if="constanciaData" class="bg-white rounded-lg shadow-lg">
      <!-- Contenido de la constancia -->
      <div ref="constanciaContent" class="p-8">
        <!-- Banner -->
        <div class="mb-8 text-center">
          <img 
            src="/images/banner-escuelas.png" 
            alt="Banner Escuelas" 
            class="mx-auto max-w-full h-auto"
            @error="handleImageError"
          />
        </div>

        <!-- Título del documento -->
        <div class="text-center mb-8">
          <h1 class="text-3xl font-bold text-gray-900 mb-2">{{ constanciaData.document }}</h1>
        </div>

        <!-- Información del estudiante -->
        <div class="mb-8 p-6 bg-gray-50 rounded-lg">
          <div class="grid grid-cols-1 gap-4">
            <div>
              <p class="text-sm text-gray-600 font-medium mb-1">Documento</p>
              <p class="text-lg text-gray-900 font-semibold">{{ constanciaData.document }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600 font-medium mb-1">Número de Control</p>
              <p class="text-lg text-gray-900 font-semibold">{{ constanciaData.idstudent }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600 font-medium mb-1">Nombre Completo</p>
              <p class="text-lg text-gray-900 font-semibold">{{ constanciaData.names }}</p>
            </div>
          </div>
        </div>

        <!-- Información del documento -->
        <div class="mb-8 p-6 bg-gray-50 rounded-lg">
          <div class="grid grid-cols-2 gap-6">
            <div>
              <div class="mb-4">
                <p class="text-sm text-gray-600 font-medium mb-1">Tipo de Documento</p>
                <p class="text-lg text-gray-900 font-semibold">{{ constanciaData.documentid }}</p>
              </div>
              <div>
                <p class="text-sm text-gray-600 font-medium mb-1">Fecha de Emisión</p>
                <p class="text-lg text-gray-900 font-semibold">{{ formatDate(constanciaData.date_create) }}</p>
              </div>
            </div>
            <div>
              <div class="mb-4">
                <p class="text-sm text-gray-600 font-medium mb-1">Fecha de Solicitud</p>
                <p class="text-lg text-gray-900 font-semibold">{{ formatDate(constanciaData.date_request) }}</p>
              </div>
              <div>
                <p class="text-sm text-gray-600 font-medium mb-1">Fecha de Entrega</p>
                <p class="text-lg text-gray-900 font-semibold">{{ formatDate(constanciaData.date_delivered) }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Sello digital -->
        <div class="mb-8 p-6 bg-blue-50 rounded-lg border-2 border-blue-200">
          <h3 class="text-lg font-bold text-gray-900 mb-3 flex items-center">
            <svg class="w-6 h-6 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
            </svg>
            Sello Digital
          </h3>
          <p class="text-center text-lg text-gray-700 font-mono break-all bg-white p-3 rounded">
            {{ guid || 'No disponible' }}
          </p>
        </div>

        <!-- Leyenda de vigencia -->
        <div class="text-center p-4">
          <p class="text-sm font-bold">
            {{ constanciaData.documentExpirationDays }} 
          </p>
        </div>
      </div>
    </div>

    <!-- Botón de exportar a PDF -->
    <div v-if="constanciaData && !error" class="p-6 flex justify-end items-center">
      <button
        @click="exportToPDF"
        :disabled="exporting"
        class="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg shadow hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <svg v-if="!exporting" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
        </svg>
        <div v-else class="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
        <span>{{ exporting ? 'Generando...' : 'Exportar a PDF' }}</span>
      </button>
    </div>

  </div>
</template>

<script>
import html2pdf from 'html2pdf.js';

export default {
  name: 'ConstanciaContent',
  props: {
    constanciaData: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    },
    guid: { 
      type: String, default: '' 
    }
  },
  data() {
    return {
      exporting: false,
    };
  },
  methods: {
    formatDate(dateString) {
      if (!dateString || dateString === '' || dateString === null) {
        return 'PENDIENTE';
      }
      
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },

    generateDigitalSeal() {
      if (!this.constanciaData) return '';
      
      // Generar un sello digital basado en los datos de la constancia
      const data = `${this.constanciaData.idstudent}-${this.constanciaData.documentId}-${this.constanciaData.date_create}`;
      
      // Simulación de hash (en producción deberías usar un hash real del backend)
      let hash = 0;
      for (let i = 0; i < data.length; i++) {
        const char = data.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
      }
      
      const hexHash = Math.abs(hash).toString(16).toUpperCase();
      const seal = `SA-${this.constanciaData.idstudent}-${hexHash}-${new Date(this.constanciaData.date_create).getTime().toString(36).toUpperCase()}`;
      
      return seal;
    },

    handleImageError(event) {
      console.error('Error al cargar la imagen del banner');
      // Ocultar la imagen si falla
      event.target.style.display = 'none';
    },

    async exportToPDF() {
      this.exporting = true;
      
      try {
        const element = this.$refs.constanciaContent;
        
        const opt = {
          margin: [10, 10, 10, 10],
          filename: `Constancia_${this.constanciaData.idstudent}_${Date.now()}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { 
            scale: 2,
            useCORS: true,
            logging: false,
            letterRendering: true
          },
          jsPDF: { 
            unit: 'mm', 
            format: 'letter', 
            orientation: 'portrait' 
          }
        };

        await html2pdf().set(opt).from(element).save();
        
        console.log('PDF generado exitosamente');
      } catch (error) {
        console.error('Error al generar el PDF:', error);
        alert('Hubo un error al generar el PDF. Por favor, intente nuevamente.');
      } finally {
        this.exporting = false;
      }
    }
  }
};
</script>